import {
  BlockStack,
  Card,
  Button,
  Collapsible,
  Box,
  InlineStack,
  Layout,
  Text,
  ProgressBar,
  SkeletonDisplayText,
  SkeletonBodyText,
} from '@shopify/polaris'
import { useSuspenseQueries } from '@tanstack/react-query'

import { useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { ChevronDownIcon, ChevronUpIcon, XIcon } from '@shopify/polaris-icons'
import {
  ChecklistItem,
  type ChecklistItemPropsWithOpen,
} from '@/components/shared/Checklist'

import {
  settingsQueryOptions,
  settingsCheckAppBlockQueryOptions,
} from '@/common/queries'
import useMutateSettings from '@/hooks/useMutateSettings'

type OnboardingItem = ChecklistItemPropsWithOpen & {}

// Deep link to add the embed block in the theme customizer
const appEmbedLink = `https://admin.shopify.com/store/${
  shopify?.config?.shop ? shopify.config.shop.split('.')[0] : ''
}/themes/current/editor?template=product&addAppBlockId=${
  import.meta.env.VITE_APP_EMBED_ID
}/bloom&target=mainSection`

const OnboardingFallback = () => {
  return (
    <Layout.Section>
      <BlockStack gap="300">
        <Card padding="400">
          <BlockStack gap="300">
            <SkeletonDisplayText size="large" />
            <SkeletonBodyText lines={2} />
            <SkeletonDisplayText size="large" />
            <SkeletonBodyText lines={2} />
            <SkeletonDisplayText size="large" />
            <SkeletonBodyText lines={2} />
          </BlockStack>
        </Card>
      </BlockStack>
    </Layout.Section>
  )
}

const Onboarding = () => {
  const navigate = useNavigate()

  const [isFinishedInit, setIsFinishedInit] = useState(false)
  const [isDismissed, setIsDismissed] = useState(false)
  const [isExpanded, setIsExpanded] = useState(true)
  const [isPolling, setIsPolling] = useState(false)
  const [onboardingItems, setOnboardingItems] = useState<
    OnboardingItem[] | null
  >(null)

  const [{ data: settings, isRefetching, isSuccess }, { data: hasAppBlock }] =
    useSuspenseQueries({
      queries: [
        settingsQueryOptions(),
        {
          ...settingsCheckAppBlockQueryOptions(),
          refetchInterval: isPolling ? 3000 : false,
          refetchIntervalInBackground: true,
        },
      ],
    })

  useEffect(() => {
    if (hasAppBlock) {
      setIsPolling(false)
    }
  }, [hasAppBlock])

  const { enabled, themeVersion, methods } = settings

  //const mentionAppBlock = enabled && !onboarded && themeVersion === '2.0'
  const isLegacy = themeVersion === 'legacy'

  const hasEnabledMethodAndDays = methods?.some((method) => {
    return method?.enabled && (method?.enabledDays || []).length > 0
  })

  const settingsMutation = useMutateSettings()

  const dismissOnboarding = () => {
    setIsDismissed(true)
    settingsMutation.mutate(
      { onboarded: true },
      {
        onSuccess: (data: any) => {
          shopify.toast.show('Setup guide dismissed', {
            duration: 10000,
            action: 'Undo',
            onAction: () => {
              setIsDismissed(false)
              settingsMutation.mutate({ onboarded: false })
              shopify.toast.hide('Setup guide dismissed')
            },
          })
          return data
        },
      }
    )
  }

  const completeCount = useMemo(
    () =>
      !onboardingItems
        ? 0
        : onboardingItems.filter((i) => i.status === 'COMPLETE').length,
    [onboardingItems]
  )
  const totalCount = useMemo(
    () => (!onboardingItems ? 0 : onboardingItems.length),
    [onboardingItems]
  )

  useEffect(() => {
    const items: OnboardingItem[] = [
      {
        id: 'activateAppEmbed',
        title: isLegacy
          ? 'Bloom script tag is active'
          : 'Activate the Bloom app block',
        description: isLegacy
          ? 'Bloom is registered in your theme.'
          : 'For Bloom to appear on your storefront, the app block must be added and saved on your product page.',
        status: isLegacy
          ? 'COMPLETE'
          : isPolling
          ? 'IN_PROGRESS'
          : hasAppBlock
          ? 'COMPLETE'
          : 'INCOMPLETE',
        isDefaultOpen: false,
        primaryButton: {
          content: isLegacy ? 'Activated' : 'Activate app block',
          props: {
            disabled: isLegacy || hasAppBlock,
            onClick: () => {
              setIsPolling(true)
              open(appEmbedLink, '_blank')
            },
          },
        },
        secondaryButton: {
          content: 'Learn more',
          props: {
            url: isLegacy
              ? 'https://docs.shopside.com.au/article/14-getting-started-with-bloom-shopify-delivery-date-picker-app'
              : 'https://docs.shopside.com.au/article/15-using-the-bloom-app-block',
            external: true,
          },
        },
      },
      {
        id: 'configureMethods',
        title: 'Configure schedule settings',
        description:
          "Please activate at least one delivery method and ensure you've configured active days.",
        status: hasEnabledMethodAndDays ? 'COMPLETE' : 'INCOMPLETE',
        isDefaultOpen: false,
        primaryButton: {
          content: 'Configure settings',
          props: {
            onClick: () => {
              navigate({
                to: `/method/${methods?.length ? methods[0].uuid : ''}`,
              })
            },
          },
        },
        secondaryButton: {
          content: 'Learn more',
          props: {
            url: 'https://docs.shopside.com.au/article/14-getting-started-with-bloom-shopify-delivery-date-picker-app',
            external: true,
          },
        },
      },
      {
        id: 'activateBloom',
        title: 'Activate Bloom',
        description: enabled
          ? 'Bloom is active on your storefront.'
          : 'Activate Bloom on your storefront to start collecting dates with your orders.',
        status:
          enabled && !isRefetching && isSuccess
            ? 'COMPLETE'
            : !enabled && !isRefetching && isSuccess
            ? 'INCOMPLETE'
            : 'IN_PROGRESS',
        isDefaultOpen: false,
        primaryButton: {
          content: enabled ? 'Deactivate' : 'Activate Bloom',
          props: {
            loading: isRefetching || settingsMutation.isPending,
            disabled: isRefetching || settingsMutation.isPending,
            onClick: () => {
              settingsMutation.mutate({ enabled: !enabled })
            },
          },
        },
      },
    ]

    // Loop over onboarding items and set isDefaultOpen for first item
    let openedIndex = -1

    items.forEach((item, i) => {
      if (openedIndex < 0) {
        if (item.status !== 'COMPLETE') {
          item.isDefaultOpen = true
          openedIndex = i
        }
      }
    })

    if (openedIndex === -1) {
      setIsExpanded(false)
    }

    // Set onboarding items
    setOnboardingItems(items)
    setIsFinishedInit(true)
  }, [settingsMutation.isPending, isRefetching, settings.enabled, isPolling])

  if (isDismissed || !onboardingItems || totalCount === 0) {
    if (isFinishedInit) {
      return null
    }

    return <OnboardingFallback />
  }

  return (
    <Layout.Section>
      <div className="onboardingCard">
        <Box position="relative">
          <Card padding="0">
            <Box position="absolute" zIndex="1" width={'100%'} padding="400">
              <div className="onboardingActions">
                <BlockStack align="center">
                  <InlineStack gap="400" blockAlign="stretch" align="end">
                    <Button
                      variant="tertiary"
                      icon={XIcon}
                      onClick={() => dismissOnboarding()}
                    />
                    <Button
                      variant="tertiary"
                      onClick={() => setIsExpanded(!isExpanded)}
                      icon={isExpanded ? ChevronUpIcon : ChevronDownIcon}
                    />
                  </InlineStack>
                </BlockStack>
              </div>
            </Box>
            <Box padding="400">
              <InlineStack gap="400" blockAlign="stretch" align="space-between">
                <BlockStack gap="200" align="start">
                  <InlineStack
                    gap="200"
                    blockAlign="stretch"
                    align="space-between"
                  >
                    <Text as="h2" variant="headingMd">
                      <span>Setup guide</span>
                    </Text>
                  </InlineStack>
                  <Text as="p">
                    <span>
                      Use this personalized guide to get your store up and
                      running.
                    </span>
                  </Text>
                  <InlineStack
                    gap="0"
                    blockAlign="stretch"
                    align="space-between"
                  >
                    <Box width="40%">
                      <Text as="p">
                        {completeCount} of {totalCount} completed
                      </Text>
                    </Box>
                    <Box width="60%" paddingBlockStart="200">
                      <ProgressBar
                        progress={(completeCount / totalCount) * 100}
                        size="small"
                        animated
                        tone={
                          completeCount === totalCount ? 'success' : 'highlight'
                        }
                      />
                    </Box>
                  </InlineStack>
                </BlockStack>
              </InlineStack>
            </Box>
            <Collapsible open={isExpanded} id="onboarding">
              <Box padding="200" paddingBlockStart="0">
                <BlockStack gap="050">
                  {onboardingItems?.length &&
                    onboardingItems.map((item) => (
                      <ChecklistItem
                        key={item.id}
                        id={item.id}
                        title={item.title}
                        description={item?.description}
                        illustration={item?.illustration}
                        status={item.status}
                        isDefaultOpen={item.isDefaultOpen}
                        primaryButton={item?.primaryButton}
                        secondaryButton={item?.secondaryButton}
                        onOpen={(id) => {
                          setOnboardingItems(
                            onboardingItems.map((i) => {
                              return {
                                ...i,
                                isDefaultOpen: id === i.id,
                              }
                            })
                          )
                        }}
                        iconAction={item.iconAction}
                      />
                    ))}
                </BlockStack>
              </Box>
            </Collapsible>
            {completeCount === totalCount && (
              <div className="onboardingFooter">
                <Button variant="secondary" onClick={() => dismissOnboarding()}>
                  Dismiss guide
                </Button>
              </div>
            )}
          </Card>
        </Box>
      </div>
    </Layout.Section>
  )
}

export { OnboardingFallback, Onboarding }
